// Adds saved report templates (the "Saved Template" dropdown and "Template Name" box) to a
// report modal. Hosting component must declare:
//   reportName        - identifies which report templates belong to; must match the report
//                       name in Reports.vue.
//   reportParameters  - the reactive parameters object bound to the report's inputs.
//
// Hosting component may implement:
//   validateReportParameters(savedParameters) - return false when the saved parameters can no
//                       longer be used (e.g. a supplier that has since been deleted). Checked
//                       before anything is applied, so an invalid template loads nothing.
//   onUserReportTemplateApplied() - called after the parameters have been applied. Needed when a
//                       widget keeps its own copy of the selection rather than reading it from
//                       reportParameters (e.g. p-tree holds `selected` flags on its nodes), so
//                       the restored values have to be pushed back into it.
//
// Call loadUserReportTemplates() from show() to reset and fetch the user's templates, and
// saveUserReportTemplate() before generating the report.

import axios from 'axios';

export default {
    data() {
        return {
            templateName: null,
            userReportTemplates: [],
            selectedUserReportTemplateId: null
        };
    },
    computed: {
        userReportTemplateOptions() {
            return this.userReportTemplates.map(userReportTemplate => ({
                ...userReportTemplate,
                text: userReportTemplate.templateName,
                value: userReportTemplate.userReportTemplateId
            }));
        },
        selectedUserReportTemplate() {
            return this.userReportTemplates.find(userReportTemplate =>
                userReportTemplate.userReportTemplateId === this.selectedUserReportTemplateId);
        },
        // Id of the loaded template when the typed name matches its name, so saving
        // overwrites it rather than creating a duplicate. Null saves a new template.
        overwriteUserReportTemplateId() {
            const trimmedTemplateName = this.templateName?.trim();

            if (!trimmedTemplateName || !this.selectedUserReportTemplate) {
                return null;
            }

            return this.selectedUserReportTemplate.templateName.toLowerCase() === trimmedTemplateName.toLowerCase()
                ? this.selectedUserReportTemplate.userReportTemplateId
                : null;
        }
    },
    methods: {
        // Clears any previous selection and reloads the user's templates for this report.
        async loadUserReportTemplates() {
            this.templateName = null;
            this.selectedUserReportTemplateId = null;
            this.userReportTemplates = [];

            if (!this.reportName) 
                return;

            await axios
                .get('/userreports/getuserreporttemplates', {
                    params: { reportName: this.reportName }
                })
                .then(result => {
                    this.userReportTemplates = result.data;
                })
                .catch(() => {
                    this.userReportTemplates = [];
                });
        },
        // Restores a saved template's settings into the report UI. Nothing is applied unless
        // all saved parameters are still valid.
        async applyUserReportTemplate(userReportTemplate) {
            if (!userReportTemplate?.parameters) {
                return;
            }

            const savedParameters = JSON.parse(userReportTemplate.parameters);

            if (this.validateReportParameters && !(await this.validateReportParameters(savedParameters))) {
                this.$appWarning('Unable to execute report template as saved. Please use report templates to create and save valid parameters.');

                // Back to "None": nothing was loaded, so the dropdown shouldn't imply otherwise.
                this.selectedUserReportTemplateId = null;
                return;
            }

            // Mutated in place rather than replaced because widgets such as
            // p-date-range-widget hold a reference to the same object.
            Object.assign(this.reportParameters, savedParameters);

            await this.onUserReportTemplateApplied?.();
        },
        // Selects and applies one of the loaded templates by id, for a caller that already knows
        // which one it wants (the My Reports list, which launches a report from a saved template).
        // loadUserReportTemplates() has to have run first, which every host's show() does.
        async applyUserReportTemplateById(userReportTemplateId) {
            const userReportTemplate = this.userReportTemplates.find(x =>
                x.userReportTemplateId === userReportTemplateId);

            if (!userReportTemplate) {
                return;
            }

            // Set first so the dropdown shows which template is loaded. applyUserReportTemplate
            // clears it again if the saved parameters turn out to be invalid.
            this.selectedUserReportTemplateId = userReportTemplate.userReportTemplateId;
            await this.applyUserReportTemplate(userReportTemplate);
        },
        // Saves the current report settings under the entered template name. Nothing is
        // saved unless a name is typed, even when a saved template is currently loaded.
        // Returns false when the save failed so the report is not generated.
        async saveUserReportTemplate() {
            const trimmedTemplateName = this.templateName?.trim();

            if (!trimmedTemplateName) {
                return true;
            }

            return await axios
                .post('/userreports/saveuserreporttemplate', {
                    userReportTemplateId: this.overwriteUserReportTemplateId,
                    reportName: this.reportName,
                    templateName: trimmedTemplateName,
                    parameters: JSON.stringify(this.reportParameters)
                })
                .then(() => true)
                .catch(() => false);
        }
    }
};
